import { youtubeThumbnailUrl } from '@recipes/domain';
import { Button } from '@recipes/ui';
import { ImagePlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { ScanPictureSection, waitUntilReady, type ScanPicture } from './scan-picture';

/**
 * The still of the YouTube video as the recipe's picture (user 2026-09-30): shown as a
 * suggestion on the review page; "Als Rezeptbild übernehmen" lets the server fetch it into a
 * permanent recipe picture, which then sits in the same slot as a kept scan photo (crop, remove).
 * The large still is tried first – not every video has one – then the one every video has.
 */
export function VideoThumbnailSection({
  videoUrl,
  value,
  onChange,
}: {
  videoUrl: string;
  value: ScanPicture | null;
  onChange: (picture: ScanPicture | null) => void;
}) {
  const { t } = useTranslation();
  const large = youtubeThumbnailUrl(videoUrl, 'max');
  const small = youtubeThumbnailUrl(videoUrl, 'hq');
  const [useSmall, setUseSmall] = useState(false);
  const [importing, setImporting] = useState(false);
  const shown = useSmall ? small : large;
  if (!shown) return null;
  if (value) return <ScanPictureSection sourceAssetIds={[]} value={value} onChange={onChange} />;

  const keep = async () => {
    setImporting(true);
    try {
      const asset = await waitUntilReady(
        (await api.media.importUrl({ url: shown, purpose: 'RECIPE' })).id,
      );
      if (asset.status !== 'READY') throw new Error(asset.errorMessage ?? 'not ready');
      onChange({ assetId: asset.id, url: asset.url });
    } catch (error) {
      toastError(error, t('ai.videoPicture.failed'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <section
      className="rounded-lg border bg-card p-4"
      aria-labelledby="video-picture-title"
      data-testid="video-picture"
    >
      <h2 id="video-picture-title" className="text-base font-semibold">
        {t('ai.videoPicture.title')}
      </h2>
      <p className="mb-3 text-sm text-muted-foreground">{t('ai.videoPicture.description')}</p>
      <div className="flex flex-wrap items-center gap-4">
        <img
          src={shown}
          alt={t('ai.videoPicture.alt')}
          className="h-28 w-48 rounded-md object-cover"
          // The large still may not exist: YouTube answers 404, the picture falls back.
          onError={() => {
            if (!useSmall) setUseSmall(true);
          }}
          data-testid="video-picture-preview"
        />
        <Button
          type="button"
          variant="outline"
          loading={importing}
          onClick={() => void keep()}
          data-testid="video-picture-use"
        >
          <ImagePlus aria-hidden /> {t('ai.videoPicture.use')}
        </Button>
      </div>
    </section>
  );
}
